import { BIOMES } from '../data/biomes.js';
import { WORLD_EVENT_NAMES } from '../data/world-events.js';
import { phrase } from '../messages/phrase.js';
/**
 * What the whole server hears of a world event (ADR 0050), by scenario: the
 * warnings, the start and the end. French lines with their templates, so a
 * client playing in English translates them (ADR 0069).
 */
/** « 5-7 », the band as the maps write it. */
function band(biome) {
    const { min, max } = BIOMES[biome].level;
    return `${String(min)}-${String(max)}`;
}
/** Where the cart sets out from: the capital, whatever the biome. */
const CAPITAL = BIOMES.meadow.village;
/** The three warnings of a scenario: in minutes, in one minute, in less than one. */
const WARNINGS = {
    invasion: {
        many: '{event} : des gobelins marchent sur {village} (niv. {band}) dans {minutes} minutes ! Tous à {village} !',
        one: '{event} : des gobelins marchent sur {village} (niv. {band}) dans une minute ! Tous à {village} !',
        last: '{event} : des gobelins marchent sur {village} (niv. {band}) dans moins d’une minute ! Tous à {village} !',
    },
    giant: {
        many: '{event} : un géant arrive aux abords de {village} (niv. {band}) dans {minutes} minutes ! Tous à {village} !',
        one: '{event} : un géant arrive aux abords de {village} (niv. {band}) dans une minute ! Tous à {village} !',
        last: '{event} : un géant arrive aux abords de {village} (niv. {band}) dans moins d’une minute ! Tous à {village} !',
    },
    convoy: {
        many: '{event} : un chariot d’or part de {capital} pour {village} (niv. {band}) dans {minutes} minutes ! Escortez-le !',
        one: '{event} : un chariot d’or part de {capital} pour {village} (niv. {band}) dans une minute ! Escortez-le !',
        last: '{event} : un chariot d’or part de {capital} pour {village} (niv. {band}) dans moins d’une minute ! Escortez-le !',
    },
};
/** `remainingMs` before it starts: whole minutes, or « moins d'une minute » at the last call. */
export function eventWarning(kind, biome, remainingMs) {
    const params = {
        event: WORLD_EVENT_NAMES[kind],
        village: BIOMES[biome].village,
        band: band(biome),
        ...(kind === 'convoy' ? { capital: CAPITAL } : {}),
    };
    const lines = WARNINGS[kind];
    const minutes = Math.round(remainingMs / 60_000);
    if (remainingMs <= 60_000)
        return phrase(lines.last, params);
    if (minutes <= 1)
        return phrase(lines.one, params);
    return phrase(lines.many, { ...params, minutes });
}
export function eventStarted(kind, biome) {
    const village = BIOMES[biome].village;
    if (kind === 'giant') {
        return phrase('Un géant rôde aux abords de {village} ! Abattez-le avant qu’il ne reparte.', {
            village,
        });
    }
    if (kind === 'convoy') {
        return phrase('Le chariot d’or quitte {capital} pour {village} ! Restez près de lui : il n’avance pas sans escorte.', { capital: CAPITAL, village });
    }
    return phrase('{village} est attaqué ! Repoussez les gobelins avant que le village ne tombe.', {
        village,
    });
}
export function eventWon(kind, biome, podium) {
    const village = BIOMES[biome].village;
    const names = podium.join(', ');
    if (kind === 'giant') {
        if (podium.length === 0)
            return phrase('Le géant de {village} est tombé !', { village });
        return phrase('Le géant de {village} est tombé ! Premiers combattants : {podium}.', {
            village,
            podium: names,
        });
    }
    if (kind === 'convoy') {
        if (podium.length === 0)
            return phrase('Le chariot d’or est arrivé à {village} !', { village });
        return phrase('Le chariot d’or est arrivé à {village} ! Premiers escorteurs : {podium}.', {
            village,
            podium: names,
        });
    }
    if (podium.length === 0)
        return phrase('Les gobelins fuient {village} : le village est sauvé !', { village });
    return phrase('{village} est sauvé ! Premiers défenseurs : {podium}.', {
        village,
        podium: names,
    });
}
/** Nobody stood where it was due when it was due: it is called off. */
export function eventCancelled(kind, biome) {
    const village = BIOMES[biome].village;
    if (kind === 'giant') {
        return phrase('Personne n’est venu affronter le géant de {village} : il est reparti, l’événement est annulé.', { village });
    }
    if (kind === 'convoy') {
        return phrase('Personne n’est venu escorter le chariot d’or vers {village} : il reste à {capital}, l’événement est annulé.', { village, capital: CAPITAL });
    }
    return phrase('Personne n’est venu défendre {village} : les gobelins ont rebroussé chemin, l’invasion est annulée.', { village });
}
export function eventLost(kind, biome) {
    const village = BIOMES[biome].village;
    if (kind === 'giant') {
        return phrase('Le géant s’éloigne de {village}, invaincu.', { village });
    }
    if (kind === 'convoy') {
        return phrase('Le chariot d’or n’a pas atteint {village} à temps : les gobelins l’ont pillé.', {
            village,
        });
    }
    return phrase('Les gobelins ont pillé {village} et repartent chargés de butin.', { village });
}
//# sourceMappingURL=world-event-lines.js.map